import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApiReturn } from '@library-shared/models/api-return';
import { EventBusEventTypeBase } from '@library-shared/services/event-bus/event-bus-event-type-base';
import { EventBusService } from '@library-shared/services/event-bus/event-bus.service';
import { GlobalErrorHandlerService } from '@library-shared/services/global-error-handler/GlobalErrorHandler.service';
import notify from 'devextreme/ui/notify';
import { Log } from '@nhustak/angular-lib';

export class BaseServiceOptions {
    loggedOutMessage? = "You have been logged out";
    resetURL? = "/logout-reset";
    loginURL? = "/login";
    logoutURL? = "/logout";
    resetPathList? = ["login", "forgotpassword", "paswordreset"];
    useReturnURL? = true;
}

@Injectable({
    providedIn: "root",
})
export class BaseService {
    static serviceOptions = new BaseServiceOptions();

    public log: any;

    requestOptions = {
        withCredentials: true,
    };

    static options(serviceOptions: BaseServiceOptions) {
        this.serviceOptions = serviceOptions;
    }

    constructor(
        public http: HttpClient,
        public router: Router,
        public eventBusService: EventBusService,
        public globalErrorHandler: GlobalErrorHandlerService
    ) {
        this.log = Log.create(this.constructor.name);
    }

    toast_warn(req: { message: string; displayTime?: number }) {
        notify(
            {
                message: req.message,
                displayTime: req.displayTime ?? 1000 * 5,
                maxWidth: 300,
                closeOnClick: true,
                hideOnOutsideClick: true,
                type: "warning",
            },
            { position: "top right" }
        );
    }

    toast_error(req: { message: string; displayTime?: number }) {
        notify(
            {
                message: req.message,
                displayTime: req.displayTime ?? 1000 * 10,
                maxWidth: 300,
                closeOnClick: true,
                hideOnOutsideClick: true,
                type: "error",
            },
            { position: "top right" }
        );
    }

    public handleFailure(data: ApiReturn, message: string = "Please retry") {
        if (!data.success) {
            this.toast_error({ message: data.message });
        }
    }

    public handleError(error: any): Promise<any> {
        this.log.error("Error in Service", error);

        if (error.status === 401) {
            // Unauthorized, bounce to login
            const url = this.router.url.toLowerCase();
            let resetURL = true;
            BaseService.serviceOptions.resetPathList.forEach((rp) => {
                if (url.search(rp.toLowerCase()) !== -1) {
                    resetURL = false;
                }
            });
            if (resetURL) {
                if (
                    url.search(
                        BaseService.serviceOptions.logoutURL.toLowerCase()
                    ) === -1
                ) {
                    // TODO: Possible multiple showings
                    this.toast_warn({
                        message: BaseService.serviceOptions.loggedOutMessage,
                    });
                    if (BaseService.serviceOptions.useReturnURL) {
                        this.router.navigate(
                            [BaseService.serviceOptions.resetURL],
                            {
                                queryParams: { returnURL: this.router.url },
                            }
                        );
                    } else {
                        this.router.navigate([
                            BaseService.serviceOptions.resetURL,
                        ]);
                    }
                } else {
                    this.router.navigate([BaseService.serviceOptions.loginURL]);
                }
            }
            this.eventBusService.emitChange(EventBusEventTypeBase.Logout);
        } else {
            this.globalErrorHandler.handleError(error);
            this.toast_error({
                message:
                    "An error has occurred.  Any changes were likely lost.  Please contact support.",
            });
        }
        // console.error('An error occurred', error); // for demo purposes only
        return Promise.reject(error.message || error);
    }
}
